import { hashKey, type QueryClient, type QueryKey, replaceEqualDeep } from '@tanstack/react-query';
import { _bumpPerf } from '../../devtool/perf-counters.js';
import { preserveLiveEventSchedule } from '../../helpers/live-event-schedule.js';
import { preserveProposalGraduation } from '../../helpers/proposal-lifecycle.js';
import {
  CONFIRMED_POSITION_ACCOUNTING_FIELDS,
  evaluateConfirmedPositionWrite,
} from './confirmed-position-transition.js';
import { supersedeOptimisticRollback } from './optimistic-rollback-owner.js';

const ENTITY_WRITE_GATE_CAP = 8192;

export type NormalizedEntityWriteSource = 'hydrate' | 'optimistic' | 'rest' | 'nats' | 'debug';

export interface NormalizedEntityWriteMeta {
  readonly source: NormalizedEntityWriteSource;
  /** When the observation began, not when its response happened to arrive. */
  readonly observedAtMs: number;
}

export interface NormalizedEntityWriteResult {
  readonly status: 'written' | 'stale';
  readonly previousSource?: NormalizedEntityWriteSource;
  readonly previousObservedAtMs?: number;
}

export interface NormalizedEntityWriteOptions {
  /** Advance the race-fence stamp without notifying subscribers when data is unchanged. */
  readonly skipEqual?: boolean;
}

interface EntityWriteStamp extends NormalizedEntityWriteMeta {
  readonly priority: number;
}

const entityWriteGates = new WeakMap<QueryClient, Map<string, EntityWriteStamp>>();

const SOURCE_PRIORITY: Readonly<Record<NormalizedEntityWriteSource, number>> = {
  hydrate: 0,
  rest: 1,
  optimistic: 2,
  nats: 3,
  debug: 4,
};

/**
 * Writes one canonical entity while fencing observations that started before
 * a newer REST, NATS, optimistic, or debug write. This closes the race where a
 * slow list request resolves after a confirmed trade and restores stale row
 * fields merely because its HTTP response arrived last.
 */
export function writeNormalizedEntitySnapshot(
  queryClient: QueryClient,
  queryKey: QueryKey,
  next: object,
  meta: NormalizedEntityWriteMeta = { source: 'hydrate', observedAtMs: 0 },
  options: NormalizedEntityWriteOptions = {},
): NormalizedEntityWriteResult {
  if (!Number.isSafeInteger(meta.observedAtMs) || meta.observedAtMs < 0) {
    throw new Error('Normalized entity observedAtMs must be a non-negative safe integer');
  }

  const confirmedPositionDecision = evaluateConfirmedPositionWrite(
    queryClient,
    queryKey,
    next,
    meta,
  );
  if (confirmedPositionDecision.status === 'pending') {
    queryClient.setQueryData(queryKey, (previous: unknown) =>
      backfillMissingEntityFields(previous, next, true, CONFIRMED_POSITION_ACCOUNTING_FIELDS),
    );
    return { status: 'stale' };
  }

  const gates = writeGates(queryClient);
  const key = hashKey(queryKey);
  const previousStamp = gates.get(key);
  const priority = SOURCE_PRIORITY[meta.source];
  if (
    previousStamp !== undefined &&
    (meta.observedAtMs < previousStamp.observedAtMs ||
      (meta.observedAtMs === previousStamp.observedAtMs && priority < previousStamp.priority))
  ) {
    if (meta.source === 'rest' || meta.source === 'hydrate') {
      const previous = queryClient.getQueryData(queryKey);
      const backfilled = backfillMissingEntityFields(previous, next, queryKey[0] === 'activity');
      const unchanged =
        options.skipEqual === true &&
        previous !== undefined &&
        replaceEqualDeep(previous, backfilled) === previous;
      if (!unchanged) queryClient.setQueryData(queryKey, backfilled);
    }
    bumpNormalizedEntityCounter(queryKey, 'staleWritesRejected', meta, previousStamp);
    return {
      status: 'stale',
      previousSource: previousStamp.source,
      previousObservedAtMs: previousStamp.observedAtMs,
    };
  }

  supersedeOptimisticRollback(queryClient, queryKey);
  const previous = queryClient.getQueryData(queryKey);
  const snapshot =
    queryKey[0] === 'proposals' && (meta.source === 'rest' || meta.source === 'hydrate')
      ? preserveProposalGraduation(previous, next)
      : queryKey[0] === 'live-events' && (meta.source === 'rest' || meta.source === 'hydrate')
        ? preserveLiveEventSchedule(previous, next)
        : next;
  const merged = mergeNormalizedEntitySnapshot(previous, snapshot);
  const unchanged =
    options.skipEqual === true &&
    previous !== undefined &&
    replaceEqualDeep(previous, merged) === previous;
  if (!unchanged) queryClient.setQueryData(queryKey, merged);
  gates.delete(key);
  gates.set(key, { ...meta, priority });
  if (gates.size > ENTITY_WRITE_GATE_CAP) {
    const oldest = gates.keys().next().value;
    if (oldest !== undefined) gates.delete(oldest);
  }
  bumpNormalizedEntityCounter(queryKey, 'writes', meta, previousStamp);
  return { status: 'written' };
}

function backfillMissingEntityFields(
  previous: unknown,
  next: object,
  enrichNulls: boolean,
  blockedFields: ReadonlySet<string> = new Set(),
): unknown {
  if (previous === null || typeof previous !== 'object') return previous;
  const previousRow = previous as Record<string, unknown>;
  const nextRow = next as Record<string, unknown>;
  const missing: Record<string, unknown> = {};
  for (const [field, value] of Object.entries(nextRow)) {
    if (blockedFields.has(field)) continue;
    if (field === 'currentPoolState') {
      const previousPool = isRecord(previousRow[field]) ? previousRow[field] : undefined;
      if (previousPool === undefined) {
        if (
          previousRow[field] === undefined ||
          (enrichNulls && previousRow[field] === null && value !== null)
        ) {
          missing[field] = value;
        }
        continue;
      }
      const mergedPool = mergeCompatibleCurrentPoolState(value, previousRow[field]);
      if (
        mergedPool !== undefined &&
        Object.keys(mergedPool).some((poolField) => !(poolField in previousPool))
      ) {
        missing[field] = mergedPool;
      }
      continue;
    }
    if (
      previousRow[field] === undefined ||
      (enrichNulls && previousRow[field] === null && value !== null)
    ) {
      missing[field] = value;
    }
  }
  return Object.keys(missing).length === 0 ? previousRow : { ...previousRow, ...missing };
}

/** Test seam; production lifetime is naturally scoped to the QueryClient. */
export function resetNormalizedEntityWriteGates(queryClient: QueryClient): void {
  entityWriteGates.delete(queryClient);
}

function writeGates(queryClient: QueryClient): Map<string, EntityWriteStamp> {
  let gates = entityWriteGates.get(queryClient);
  if (gates === undefined) {
    gates = new Map<string, EntityWriteStamp>();
    entityWriteGates.set(queryClient, gates);
  }
  return gates;
}

export function mergeNormalizedEntitySnapshot(previous: unknown, next: object): object {
  if (previous === null || typeof previous !== 'object') return next;
  const previousRow = previous as Record<string, unknown>;
  const nextRow = next as Record<string, unknown>;
  const previousOutcomes = previousRow.outcomes;
  const nextOutcomes = nextRow.outcomes;
  const currentPoolState = mergeCompatibleCurrentPoolState(
    previousRow.currentPoolState,
    nextRow.currentPoolState,
  );
  return {
    ...previousRow,
    ...nextRow,
    ...(Array.isArray(previousOutcomes) && Array.isArray(nextOutcomes)
      ? { outcomes: mergeOutcomeSnapshots(previousOutcomes, nextOutcomes) }
      : {}),
    ...mergeParticipantIdentitySnapshots(previousRow, nextRow),
    ...(currentPoolState === undefined ? {} : { currentPoolState }),
  };
}

function mergeCompatibleCurrentPoolState(
  base: unknown,
  overlay: unknown,
): Record<string, unknown> | undefined {
  if (!isRecord(base) || !isRecord(overlay)) return undefined;
  const baseFamily = poolFamily(base);
  if (baseFamily === null || baseFamily !== poolFamily(overlay)) return undefined;
  return { ...base, ...overlay };
}

function poolFamily(pool: Readonly<Record<string, unknown>>): 'bonding-curve' | 'cp-swap' | null {
  if ('virtualQuoteReserveD6' in pool) return 'bonding-curve';
  if ('quoteVaultReserveD6' in pool) return 'cp-swap';
  return null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function mergeParticipantIdentitySnapshots(
  previous: Readonly<Record<string, unknown>>,
  next: Readonly<Record<string, unknown>>,
): Record<string, unknown> {
  const merged: Record<string, unknown> = {};
  for (const field of ['trader', 'backer', 'booster']) {
    const prior = previous[field];
    const incoming = next[field];
    if (
      prior !== null &&
      typeof prior === 'object' &&
      incoming !== null &&
      typeof incoming === 'object'
    ) {
      merged[field] = { ...prior, ...incoming };
    }
  }
  return merged;
}

function mergeOutcomeSnapshots(
  previous: readonly unknown[],
  next: readonly unknown[],
): readonly unknown[] {
  const previousById = new Map<string, Record<string, unknown>>();
  for (const outcome of previous) {
    if (outcome === null || typeof outcome !== 'object') continue;
    const row = outcome as Record<string, unknown>;
    const id = row.outcomeIndex ?? row.mint;
    if (id !== undefined) previousById.set(String(id), row);
  }
  return next.map((outcome) => {
    if (outcome === null || typeof outcome !== 'object') return outcome;
    const row = outcome as Record<string, unknown>;
    const id = row.outcomeIndex ?? row.mint;
    const prior = id === undefined ? undefined : previousById.get(String(id));
    return prior === undefined ? outcome : { ...prior, ...row };
  });
}

function bumpNormalizedEntityCounter(
  queryKey: QueryKey,
  counter: 'writes' | 'staleWritesRejected',
  meta: NormalizedEntityWriteMeta,
  previousStamp: EntityWriteStamp | undefined,
): void {
  const resource = queryKey[0];
  if (queryKey[1] !== 'entity') return;
  if (process.env.NODE_ENV !== 'production') {
    if (resource === 'positions') _bumpPerf(`positionEntity.${counter}`);
    if (resource === 'trades' && meta.source === 'rest' && previousStamp?.source === 'nats') {
      _bumpPerf('tradeEntity.authoritativeUpgrades');
    }
  }
}
